import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { adminSubmissionsService } from '@/features/admin/services/admin.submissions.service'
import type { ApplicationSubmissionDocument } from '@/types'

interface Props {
  /** Tech submission id, or null to close the modal. */
  techSubmissionId: string | null
  /** Intake submission (for context in the header — "Tech for {applicant name}") */
  intakeApplicantName: string
  onClose: () => void
}

/**
 * Read-only view of a tech-questionnaire submission. Shown when a curator
 * clicks the "View" affordance on the Participants tab's Tech column.
 * Reuses the same image-thumbnail + text-link file rendering as the curation
 * detail modal but skips all the editable bits (no notes, no comments, no
 * status moves) — tech submissions aren't curated.
 */
export function TechSubmissionViewModal({
  techSubmissionId,
  intakeApplicantName,
  onClose,
}: Props) {
  const open = techSubmissionId !== null
  const [submission, setSubmission] = useState<(ApplicationSubmissionDocument & { id: string }) | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!techSubmissionId) {
      setSubmission(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    adminSubmissionsService
      .getById(techSubmissionId)
      .then((data) => {
        if (cancelled) return
        if (!data) {
          setError(withErrorCode('Tech submission not found.', 'app/validation'))
        } else {
          setSubmission(data)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(formatUserFacingError(err, 'Failed to load tech submission.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [techSubmissionId])

  const answersList = submission ? Object.entries(submission.answers ?? {}) : []
  const fileEntries = submission
    ? Object.entries(submission.fileUrls ?? {}).filter(([, urls]) => urls.length > 0)
    : []

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tech Info — {intakeApplicantName}</DialogTitle>
          <DialogDescription>
            Read-only view of what the artist submitted on their tech questionnaire.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {error && !loading && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        {submission && !loading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 px-4 py-3 text-sm sm:grid-cols-3">
              <Meta label="Submitted" value={formatTs(submission.submittedAt)} />
              <Meta label="Status" value={submission.status} />
              <Meta
                label="Files"
                value={fileEntries.reduce((n, [, urls]) => n + urls.length, 0)}
              />
            </div>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Answers
              </h3>
              {answersList.length === 0 ? (
                <p className="text-sm text-muted-foreground">No answers recorded.</p>
              ) : (
                <dl className="space-y-3">
                  {answersList.map(([fieldId, answer]) => (
                    <div key={fieldId} className="space-y-0.5">
                      <dt className="text-xs font-medium text-muted-foreground">{answer.label}</dt>
                      <dd className="whitespace-pre-wrap text-sm">{renderValue(answer.value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            {fileEntries.length > 0 && (
              <>
                <Separator />
                <section className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    File Uploads
                  </h3>
                  <div className="space-y-4">
                    {fileEntries.map(([fieldId, urls]) => {
                      const label = submission.answers?.[fieldId]?.label ?? fieldId
                      return (
                        <div key={fieldId} className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">{label}</p>
                          <div className="flex flex-wrap gap-2">
                            {urls.map((url) => (
                              <FilePreview key={url} url={url} />
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </section>
              </>
            )}
          </div>
        )}

        {!techSubmissionId && !loading && !error && (
          <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Opening…
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function FilePreview({ url }: { url: string }) {
  const filename = filenameFromUrl(url)
  const isImage = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(filename)

  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={filename}
        className="group block overflow-hidden rounded-md border bg-muted/30 transition-opacity hover:opacity-90"
      >
        <img
          src={url}
          alt={filename}
          className="h-28 w-28 object-cover"
          onError={(e) => {
            const target = e.currentTarget
            target.style.display = 'none'
            const fallback = target.nextElementSibling as HTMLElement | null
            if (fallback) fallback.style.display = 'flex'
          }}
        />
        <div
          className="hidden h-28 w-28 items-center justify-center text-xs text-muted-foreground"
          style={{ display: 'none' }}
        >
          <span className="px-2 text-center">{filename}</span>
        </div>
      </a>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm text-primary hover:bg-muted/40 hover:underline"
    >
      {filename}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function filenameFromUrl(url: string): string {
  try {
    const decoded = decodeURIComponent(url.split('?')[0] ?? url)
    return decoded.split('/').pop() || url
  } catch {
    return url
  }
}

function formatTs(ts: { toMillis?: () => number } | undefined): string {
  const ms = ts?.toMillis?.()
  if (!ms) return '—'
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
